import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
    Box,
    Button,
    Backdrop,
    Fade,
    Modal,
    SelectChangeEvent,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { editUser, editUserPermissions, getUser, getUserPermissions, passwordReset } from 'src/api/userManagement';
import { useParams } from 'react-router-dom';
import UserDetails from './UserDetails';
import { EntityAccess, UserManagementUser, UserPermission, LocationInfo, FmtpCustomer, Permissions } from '@lanterds/types';
import './User.scss';
import { AuthTypes, EditUserRequestBody, SelectedPermission, UserForm } from './types';
import { PermissionsTable } from './PermissionsTable';
import { mapPermissionOptions, filterPermissionOptions, getUserEmailFromToken } from 'src/util/helpers';
import PermissionsFlow from './PermissionsFlow/PermissionsFlow';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import { useSnackbar, SnackbarKey } from 'notistack';

export default function User() {
    const fmtpNavigate = useFmtpNavigate();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { userId = '' } = useParams();

    const [userData, setUserData] = useState<UserManagementUser | null>(null);
    const [formErrors, setFormErrors] = useState<Partial<UserForm>>({});
    const [userForm, setUserForm] = useState<UserForm | null>(null);
    const [userPermissions, setUserPermissions] = useState<Partial<Record<Permissions, boolean | EntityAccess[]>> | null>(null);
    const [localCustomerAccessList, setLocalCustomerAccessList] = useState<FmtpCustomer[] | null>(null);
    const [localDealerAccessList, setLocalDealerAccessList] = useState<LocationInfo[] | null>(null);
    const [open, setOpen] = useState(false);
    const [changedPermissions, setChangedPermissions] = useState<Partial<UserPermission> | null>(null);
    const saveSnackbarRef = useRef<SnackbarKey | null>(null);
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSelectedPermissions([]);
    };

    const loggedInUserEmail = getUserEmailFromToken();
    const storedPermissions = sessionStorage.getItem(loggedInUserEmail);
    const cachedPermissions = JSON.parse(storedPermissions ?? '{}');

    const [selectedPermissions, setSelectedPermissions] = useState<SelectedPermission[]>([]);

    const { data: fetchedUserPermissions, isFetching: isUserPermissionsFetching } = getUserPermissions(
        userId,
        !!userId && !userPermissions,
        [userId],
    );

    const { mutateAsync: updatePermissionsAsync, isPending: isUpdatePermissionsPending } = editUserPermissions(userId);
    const { mutateAsync: updateUserAsync, isPending: isEditUserPending } = editUser(userForm ?? ({} as UserForm), false);
    const { mutateAsync: resetPassword } = passwordReset(userData?.email ?? '');
    const { data: fetchedUserData, isFetching: isFetchingUser } = getUser(userId ?? '');
    const loadingUserRef = useRef<SnackbarKey | null>(null);
    const loadingPermsRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        if (isFetchingUser) {
            if (loadingUserRef.current) closeSnackbar(loadingUserRef.current);
            loadingUserRef.current = enqueueSnackbar('Loading user…', {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        } else if (loadingUserRef.current) {
            closeSnackbar(loadingUserRef.current);
            loadingUserRef.current = null;
        }
    }, [isFetchingUser, enqueueSnackbar, closeSnackbar]);

    useEffect(() => {
        if (isUserPermissionsFetching) {
            if (loadingPermsRef.current) closeSnackbar(loadingPermsRef.current);
            loadingPermsRef.current = enqueueSnackbar('Loading user permissions…', {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        } else if (loadingPermsRef.current) {
            closeSnackbar(loadingPermsRef.current);
            loadingPermsRef.current = null;
        }
    }, [isUserPermissionsFetching, enqueueSnackbar, closeSnackbar]);

    useEffect(() => {
        if (userData) {
            const { firstName, lastName, email, isFederated } = userData;
            setUserForm({
                firstName,
                lastName,
                email,
                isFederated,
            });
        }
    }, [userData]);

    useEffect(() => {
        if (fetchedUserData) {
            setUserData(fetchedUserData);
        }
    }, [fetchedUserData, isFetchingUser]);

    useEffect(() => {
        if (fetchedUserPermissions) {
            setUserPermissions(fetchedUserPermissions.permissions);
            setLocalCustomerAccessList(fetchedUserPermissions.customerAccessList);
            setLocalDealerAccessList(fetchedUserPermissions.dealerAccessList);
        }
    }, [fetchedUserPermissions, isUserPermissionsFetching]);

    const handleConfirmResetPassword = async () => {
        setResetPasswordDialogOpen(false);
        const response = await resetPassword();
        if (response) {
            enqueueSnackbar('Password reset email sent!', { variant: 'success' });
        } else {
            enqueueSnackbar('Failed to send password reset email.', { variant: 'error' });
        }
    };

    const handleResetPasswordClick = async () => {
        setResetPasswordDialogOpen(true);
    };

    const validateFields = () => {
        const tempFormErrors: Partial<UserForm> = {};

        if (!userForm?.firstName) {
            tempFormErrors.firstName = 'First Name cannot be blank.';
        }

        if (!userForm?.lastName) {
            tempFormErrors.lastName = 'Last Name cannot be blank.';
        }

        setFormErrors(tempFormErrors);
        return tempFormErrors;
    };

    const onClickSave = async () => {
        const validationResults = validateFields();

        if (!validationResults?.firstName && !validationResults?.lastName) {
            // Should never be null here with form validation, but need to check to satisfy TS
            saveSnackbarRef.current = enqueueSnackbar('Updating user…', {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
            try {
                if (
                    userForm &&
                    (userData?.firstName !== userForm.firstName ||
                        userData?.lastName !== userForm.lastName ||
                        userData?.isFederated !== userForm.isFederated)
                ) {
                    // Update user's info
                    await updateUserAsync({ ...userForm } as EditUserRequestBody);
                    setUserData({
                        ...userData,
                        firstName: userForm.firstName,
                        lastName: userForm?.lastName,
                        isFederated: userForm.isFederated,
                    } as UserManagementUser);
                }

                if (changedPermissions && Object.keys(changedPermissions).length) {
                    await updatePermissionsAsync(userPermissions);
                }
                closeSnackbar();
                enqueueSnackbar('User successfully updated!', { variant: 'success' });
                setChangedPermissions(null);
            } catch (err) {
                console.error(err);
                if (saveSnackbarRef.current) closeSnackbar(saveSnackbarRef.current);
                enqueueSnackbar('Failed to update user.', { variant: 'error' });
            } finally {
                saveSnackbarRef.current = null;
            }
        }
    };

    const onChangeFormInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setUserForm({ ...userForm, [name]: value } as UserForm);
    };

    const onChangeBooleanSelection = (e: SelectChangeEvent<AuthTypes>) => {
        const { name, value } = e.target;

        setUserForm({ ...userForm, [name]: value === AuthTypes.External } as UserForm);
    };

    const onAddedPermissions = ({
        userPermissions: newUserPermissions,
        customerAccessList,
        dealerAccessList,
    }: {
        userPermissions?: UserPermission;
        customerAccessList?: FmtpCustomer[];
        dealerAccessList?: LocationInfo[];
    }) => {
        const tempPermissions: Partial<UserPermission> = changedPermissions ? { ...changedPermissions } : {};
        for (const selectedPermission of selectedPermissions) {
            tempPermissions[selectedPermission.name] = true;
        }
        setChangedPermissions(tempPermissions);
        setUserPermissions(newUserPermissions ?? {});
        setLocalCustomerAccessList([...(localCustomerAccessList ?? []), ...(customerAccessList ?? [])]);
        setLocalDealerAccessList([...(localDealerAccessList ?? []), ...(dealerAccessList ?? [])]);
        handleClose();
    };

    return (
        <>
            <Box id="user-container" className="auto-fill-height">
                <Card className="auto-fill-height">
                    <Grid container spacing={2} style={{ padding: '10px' }}>
                        <Grid xs={12}>
                            <Button
                                variant="text"
                                startIcon={<NavigateNextIcon sx={{ transform: 'rotate(180deg)' }} />}
                                onClick={() => fmtpNavigate('/admin/users')}
                            >
                                Back
                            </Button>
                        </Grid>
                    </Grid>
                    <Box className="form-and-table auto-fill-height">
                        <Grid container spacing={2} className="user-form auto-fill-height">
                            <Grid xl={11} lg={11} md={11} sm={12} xs={12}>
                                <Grid xs={12}>
                                    <UserDetails
                                        user={userForm ?? ({} as UserForm)}
                                        formErrors={formErrors}
                                        onChangeInput={onChangeFormInput}
                                        onChangeBooleanSelection={onChangeBooleanSelection}
                                        isEditUserPending={isEditUserPending}
                                    />
                                </Grid>
                                {/* Using non breaking space in grid to create space between user details and buttons */}
                                <Grid xs={12}>&nbsp;</Grid>
                                <Grid xs={12} container columnGap={2} justifyContent="center">
                                    <Grid>
                                        <Button
                                            size="medium"
                                            variant="contained"
                                            onClick={handleResetPasswordClick}
                                            classes={`${
                                                userData?.isFederated === undefined || userData.isFederated ? 'hide' : ''
                                            }`}
                                        >
                                            Reset Password
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        <Button size="medium" variant="contained" onClick={handleOpen}>
                                            Add Permissions
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        <Button size="medium" variant="contained" onClick={onClickSave}>
                                            Save
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <CardContent className="card-content auto-fill-height">
                            <PermissionsTable
                                userId={userData?.email ?? ''}
                                userPermissions={{
                                    permissions: userPermissions ?? {},
                                    customerAccessList: localCustomerAccessList,
                                    dealerAccessList: localDealerAccessList,
                                    userId,
                                }}
                                setUserPermissions={setUserPermissions}
                                changedPermissions={changedPermissions}
                                isLoading={isFetchingUser || isUserPermissionsFetching}
                            />
                        </CardContent>
                    </Box>
                </Card>
            </Box>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box id="permissions-modal">
                        <PermissionsFlow
                            permissionsOptions={mapPermissionOptions(
                                filterPermissionOptions(cachedPermissions, userPermissions ? userPermissions : null),
                            )}
                            fetchedUserPermissions={{
                                permissions: userPermissions ?? {},
                                customerAccessList: localCustomerAccessList,
                                dealerAccessList: localDealerAccessList,
                                userId,
                            }}
                            selectedPermissions={selectedPermissions}
                            setSelectedPermissions={setSelectedPermissions}
                            isUserPermissionsFetching={isUserPermissionsFetching}
                            adminsPermissions={cachedPermissions}
                            canGetCustomers={open && !!selectedPermissions.length}
                            onAddedPermissions={onAddedPermissions}
                        />
                    </Box>
                </Fade>
            </Modal>
            <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
                <DialogTitle>Password Reset</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to send this user a Password Reset email?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmResetPassword}>
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
